/**
 * Brush & Eraser Tools - Photoshop-like drawing tools for T-Shirt Design Studio
 * Features: Freehand drawing, smoothing, eraser, brush layer management
 */

(function() {
    'use strict';

    class BrushTool {
        constructor(sectionId, layerManager) {
            this.sectionId = sectionId;
            this.layerManager = layerManager;
            this.activeTool = null; // 'brush' | 'eraser' | null
            this.isDrawing = false;
            this.currentBrushLayer = null;
            this.currentPath = null;
            this.paths = []; // Array of paths for undo/redo
            
            // Brush settings
            this.brushSize = 20;
            this.brushOpacity = 100;
            this.brushSmoothing = 0.3; // 0-1, higher = smoother
            this.brushColor = '#000000';
            
            // Eraser settings
            this.eraserSize = 20;
            this.eraserMode = 'stroke'; // 'stroke' or 'mask' (mask is Phase 2)
            
            // Performance optimization
            this.lastDrawTime = 0;
            this.drawThrottle = 16; // ~60fps max
            this.maxCanvasSize = 2048; // Max canvas dimension
            
            // Smoothing/Stabilization
            this.points = [];
            this.smoothingBuffer = [];
            
            this.init();
        }

        init() {
            this.setupKeyboardShortcuts();
            this.setupToolButtons();
            console.log('✅ Brush Tool initialized');
        }

        /**
         * Create a new brush layer
         */
        createBrushLayer(properties = {}) {
            const layerId = `layer-${this.layerManager.nextLayerId++}`;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set canvas size based on container
            const container = document.getElementById(this.layerManager.containerId);
            if (!container) return null;
            
            const containerRect = container.getBoundingClientRect();
            const width = Math.min(containerRect.width, this.maxCanvasSize);
            const height = Math.min(containerRect.height, this.maxCanvasSize);
            
            canvas.width = width;
            canvas.height = height;
            canvas.style.position = 'absolute';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.pointerEvents = 'auto';
            canvas.style.cursor = this.activeTool === 'brush' ? 'crosshair' : 'crosshair';
            canvas.className = `cts-brush-layer-${this.sectionId}`;
            canvas.id = layerId;
            
            // Set canvas background to transparent
            ctx.clearRect(0, 0, width, height);
            
            const layer = {
                id: layerId,
                type: 'brush',
                name: properties.name || `Brush Layer ${this.layerManager.layers.filter(l => l.type === 'brush').length + 1}`,
                visible: properties.visible !== false,
                locked: properties.locked === true,
                opacity: properties.opacity !== undefined ? properties.opacity : 100,
                blendMode: properties.blendMode || 'normal',
                zIndex: this.layerManager.layers.length,
                
                // Canvas properties
                canvas: canvas,
                ctx: ctx,
                width: width,
                height: height,
                
                // Brush data
                paths: [], // Array of path objects for undo/redo
                
                // Transform properties
                x: 0,
                y: 0,
                rotation: 0,
                scaleX: 1,
                scaleY: 1,
                
                // DOM element reference
                element: canvas
            };
            
            // Add layer to layer manager
            this.layerManager.layers.push(layer);
            this.layerManager.saveState();
            this.layerManager.renderLayers();
            this.layerManager.updateLayersPanel();
            
            // Store reference
            this.currentBrushLayer = layer;
            
            return layerId;
        }

        /**
         * Activate brush tool
         */
        activateBrush() {
            this.activeTool = 'brush';
            this.updateCursor();
            this.updateToolButtons();
            
            // Create brush layer if none exists or if current is locked
            const brushLayers = this.layerManager.layers.filter(l => l.type === 'brush');
            if (brushLayers.length === 0 || brushLayers.every(l => l.locked)) {
                const layerId = this.createBrushLayer();
                this.currentBrushLayer = this.layerManager.getLayer(layerId);
            } else {
                // Use the most recent brush layer
                this.currentBrushLayer = brushLayers[brushLayers.length - 1];
            }
            
            this.setupCanvasEvents();
        }

        /**
         * Activate eraser tool
         */
        activateEraser() {
            this.activeTool = 'eraser';
            this.updateCursor();
            this.updateToolButtons();
            this.setupCanvasEvents();
        }

        /**
         * Deactivate tools (switch to move/select)
         */
        deactivate() {
            this.activeTool = null;
            this.isDrawing = false;
            this.currentPath = null;
            this.points = [];
            this.smoothingBuffer = [];
            this.updateCursor();
            this.updateToolButtons();
            this.removeCanvasEvents();
        }

        /**
         * Setup canvas drawing events
         */
        setupCanvasEvents() {
            const container = document.getElementById(this.layerManager.containerId);
            if (!container) return;
            
            // Remove existing listeners to avoid duplicates
            this.removeCanvasEvents();
            
            // Mouse events
            container.addEventListener('mousedown', this.handleStart.bind(this));
            container.addEventListener('mousemove', this.handleMove.bind(this));
            container.addEventListener('mouseup', this.handleEnd.bind(this));
            container.addEventListener('mouseleave', this.handleEnd.bind(this));
            
            // Touch events
            container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
            container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
            container.addEventListener('touchend', this.handleTouchEnd.bind(this));
            container.addEventListener('touchcancel', this.handleTouchEnd.bind(this));
            
            this.canvasEventsSetup = true;
        }

        /**
         * Remove canvas drawing events
         */
        removeCanvasEvents() {
            const container = document.getElementById(this.layerManager.containerId);
            if (!container || !this.canvasEventsSetup) return;
            
            container.removeEventListener('mousedown', this.handleStart);
            container.removeEventListener('mousemove', this.handleMove);
            container.removeEventListener('mouseup', this.handleEnd);
            container.removeEventListener('mouseleave', this.handleEnd);
            container.removeEventListener('touchstart', this.handleTouchStart);
            container.removeEventListener('touchmove', this.handleTouchMove);
            container.removeEventListener('touchend', this.handleTouchEnd);
            container.removeEventListener('touchcancel', this.handleTouchEnd);
            
            this.canvasEventsSetup = false;
        }

        /**
         * Get coordinates relative to canvas
         */
        getCanvasCoordinates(e, canvas) {
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            
            let clientX, clientY;
            
            if (e.touches && e.touches.length > 0) {
                clientX = e.touches[0].clientX;
                clientY = e.touches[0].clientY;
            } else {
                clientX = e.clientX;
                clientY = e.clientY;
            }
            
            return {
                x: (clientX - rect.left) * scaleX,
                y: (clientY - rect.top) * scaleY
            };
        }

        /**
         * Handle drawing start
         */
        handleStart(e) {
            if (this.activeTool !== 'brush' && this.activeTool !== 'eraser') return;
            if (!this.currentBrushLayer || this.currentBrushLayer.locked) return;
            
            e.preventDefault();
            this.isDrawing = true;
            
            const coords = this.getCanvasCoordinates(e, this.currentBrushLayer.canvas);
            this.points = [coords];
            this.smoothingBuffer = [coords];
            
            if (this.activeTool === 'brush') {
                this.startBrushStroke(coords);
            } else if (this.activeTool === 'eraser') {
                this.startEraserStroke(coords);
            }
        }

        /**
         * Handle drawing move
         */
        handleMove(e) {
            if (!this.isDrawing) return;
            if (this.activeTool !== 'brush' && this.activeTool !== 'eraser') return;
            
            e.preventDefault();
            
            const now = Date.now();
            if (now - this.lastDrawTime < this.drawThrottle) return;
            this.lastDrawTime = now;
            
            const coords = this.getCanvasCoordinates(e, this.currentBrushLayer.canvas);
            
            // Apply smoothing
            const smoothed = this.applySmoothing(coords);
            this.points.push(smoothed);
            
            if (this.activeTool === 'brush') {
                this.continueBrushStroke(smoothed);
            } else if (this.activeTool === 'eraser') {
                this.continueEraserStroke(smoothed);
            }
        }

        /**
         * Handle drawing end
         */
        handleEnd(e) {
            if (!this.isDrawing) return;
            
            e.preventDefault();
            this.isDrawing = false;
            
            if (this.activeTool === 'brush') {
                this.endBrushStroke();
            } else if (this.activeTool === 'eraser') {
                this.endEraserStroke();
            }
            
            this.points = [];
            this.smoothingBuffer = [];
            this.layerManager.saveState();
        }

        /**
         * Touch event handlers
         */
        handleTouchStart(e) {
            this.handleStart(e);
        }

        handleTouchMove(e) {
            this.handleMove(e);
        }

        handleTouchEnd(e) {
            this.handleEnd(e);
        }

        /**
         * Apply smoothing/stabilization to coordinates
         */
        applySmoothing(point) {
            if (this.brushSmoothing === 0) return point;
            
            this.smoothingBuffer.push(point);
            
            // Keep only last few points for smoothing
            if (this.smoothingBuffer.length > 5) {
                this.smoothingBuffer.shift();
            }
            
            // Calculate weighted average
            let sumX = 0, sumY = 0, weight = 0;
            const bufferLength = this.smoothingBuffer.length;
            
            for (let i = 0; i < bufferLength; i++) {
                const w = (i + 1) / bufferLength; // More weight to recent points
                sumX += this.smoothingBuffer[i].x * w;
                sumY += this.smoothingBuffer[i].y * w;
                weight += w;
            }
            
            return {
                x: sumX / weight,
                y: sumY / weight
            };
        }

        /**
         * Start brush stroke
         */
        startBrushStroke(coords) {
            const layer = this.currentBrushLayer;
            const ctx = layer.ctx;
            
            ctx.globalAlpha = this.brushOpacity / 100;
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = this.brushColor;
            ctx.lineWidth = this.brushSize;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
            
            this.currentPath = {
                type: 'brush',
                points: [coords],
                color: this.brushColor,
                size: this.brushSize,
                opacity: this.brushOpacity
            };
        }

        /**
         * Continue brush stroke
         */
        continueBrushStroke(coords) {
            const layer = this.currentBrushLayer;
            const ctx = layer.ctx;
            
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            
            if (this.currentPath) {
                this.currentPath.points.push(coords);
            }
        }

        /**
         * End brush stroke
         */
        endBrushStroke() {
            if (this.currentPath && this.currentPath.points.length > 0) {
                this.currentBrushLayer.paths.push(this.currentPath);
            }
            this.currentPath = null;
        }

        /**
         * Start eraser stroke
         */
        startEraserStroke(coords) {
            const layer = this.currentBrushLayer;
            
            if (this.eraserMode === 'stroke') {
                // Find and mark stroke for deletion
                this.findStrokeAtPoint(coords);
            } else {
                // Mask-based erasing (Phase 2)
                const ctx = layer.ctx;
                ctx.globalCompositeOperation = 'destination-out';
                ctx.lineWidth = this.eraserSize;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.beginPath();
                ctx.moveTo(coords.x, coords.y);
            }
        }

        /**
         * Continue eraser stroke
         */
        continueEraserStroke(coords) {
            const layer = this.currentBrushLayer;
            
            if (this.eraserMode === 'mask') {
                const ctx = layer.ctx;
                ctx.lineTo(coords.x, coords.y);
                ctx.stroke();
            }
        }

        /**
         * End eraser stroke
         */
        endEraserStroke() {
            if (this.eraserMode === 'stroke') {
                // Remove marked strokes
                this.removeMarkedStrokes();
            }
        }

        /**
         * Find stroke at point (for stroke eraser)
         */
        findStrokeAtPoint(point) {
            const layer = this.currentBrushLayer;
            const threshold = this.eraserSize / 2;
            
            for (let i = layer.paths.length - 1; i >= 0; i--) {
                const path = layer.paths[i];
                for (let j = 0; j < path.points.length; j++) {
                    const p = path.points[j];
                    const dist = Math.sqrt(
                        Math.pow(p.x - point.x, 2) + Math.pow(p.y - point.y, 2)
                    );
                    
                    if (dist < threshold + path.size / 2) {
                        path.markedForDeletion = true;
                        return;
                    }
                }
            }
        }

        /**
         * Remove marked strokes
         */
        removeMarkedStrokes() {
            const layer = this.currentBrushLayer;
            const pathsToRemove = layer.paths.filter(p => p.markedForDeletion);
            
            if (pathsToRemove.length === 0) return;
            
            // Redraw canvas without marked paths
            layer.ctx.clearRect(0, 0, layer.width, layer.height);
            
            layer.paths = layer.paths.filter(p => !p.markedForDeletion);
            
            // Redraw all remaining paths
            layer.paths.forEach(path => {
                this.redrawPath(layer, path);
            });
        }

        /**
         * Redraw a path on canvas
         */
        redrawPath(layer, path) {
            const ctx = layer.ctx;
            
            if (path.type === 'brush') {
                ctx.globalAlpha = path.opacity / 100;
                ctx.strokeStyle = path.color;
                ctx.lineWidth = path.size;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                
                ctx.beginPath();
                ctx.moveTo(path.points[0].x, path.points[0].y);
                
                for (let i = 1; i < path.points.length; i++) {
                    ctx.lineTo(path.points[i].x, path.points[i].y);
                }
                
                ctx.stroke();
            }
        }

        /**
         * Update cursor based on active tool
         */
        updateCursor() {
            const container = document.getElementById(this.layerManager.containerId);
            if (!container) return;
            
            if (this.activeTool === 'brush') {
                container.style.cursor = 'crosshair';
            } else if (this.activeTool === 'eraser') {
                container.style.cursor = 'grab';
            } else {
                container.style.cursor = 'default';
            }
        }

        /**
         * Setup keyboard shortcuts
         */
        setupKeyboardShortcuts() {
            document.addEventListener('keydown', (e) => {
                const studio = document.getElementById(`custom-tshirt-studio-${this.sectionId}`);
                if (!studio || studio.style.display === 'none') return;
                
                const activeElement = document.activeElement;
                if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                    return; // Don't intercept when typing
                }
                
                // B = Brush
                if (e.key === 'b' || e.key === 'B') {
                    e.preventDefault();
                    this.activateBrush();
                    return;
                }
                
                // E = Eraser
                if (e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                    this.activateEraser();
                    return;
                }
                
                // V = Move/Select (deactivate brush/eraser)
                if (e.key === 'v' || e.key === 'V') {
                    e.preventDefault();
                    this.deactivate();
                    return;
                }
                
                // [ = Decrease brush size
                if (e.key === '[') {
                    e.preventDefault();
                    this.setBrushSize(Math.max(1, this.brushSize - 2));
                    return;
                }
                
                // ] = Increase brush size
                if (e.key === ']') {
                    e.preventDefault();
                    this.setBrushSize(Math.min(200, this.brushSize + 2));
                    return;
                }
            });
        }

        /**
         * Setup tool buttons
         */
        setupToolButtons() {
            const brushBtn = document.getElementById(`brushToolBtn-${this.sectionId}`);
            const eraserBtn = document.getElementById(`eraserToolBtn-${this.sectionId}`);
            const moveBtn = document.getElementById(`moveToolBtn-${this.sectionId}`);
            
            if (brushBtn) {
                brushBtn.addEventListener('click', () => this.activateBrush());
            }
            
            if (eraserBtn) {
                eraserBtn.addEventListener('click', () => this.activateEraser());
            }
            
            if (moveBtn) {
                moveBtn.addEventListener('click', () => this.deactivate());
            }
        }

        /**
         * Update tool button states
         */
        updateToolButtons() {
            const brushBtn = document.getElementById(`brushToolBtn-${this.sectionId}`);
            const eraserBtn = document.getElementById(`eraserToolBtn-${this.sectionId}`);
            const moveBtn = document.getElementById(`moveToolBtn-${this.sectionId}`);
            
            [brushBtn, eraserBtn, moveBtn].forEach(btn => {
                if (btn) btn.classList.remove('active');
            });
            
            if (this.activeTool === 'brush' && brushBtn) {
                brushBtn.classList.add('active');
            } else if (this.activeTool === 'eraser' && eraserBtn) {
                eraserBtn.classList.add('active');
            } else if (moveBtn) {
                moveBtn.classList.add('active');
            }
        }

        /**
         * Set brush size
         */
        setBrushSize(size) {
            this.brushSize = Math.max(1, Math.min(200, size));
            this.updateBrushSizeDisplay();
        }

        /**
         * Set brush opacity
         */
        setBrushOpacity(opacity) {
            this.brushOpacity = Math.max(0, Math.min(100, opacity));
            this.updateBrushOpacityDisplay();
        }

        /**
         * Set brush smoothing
         */
        setBrushSmoothing(smoothing) {
            this.brushSmoothing = Math.max(0, Math.min(1, smoothing));
            this.updateBrushSmoothingDisplay();
        }

        /**
         * Set brush color
         */
        setBrushColor(color) {
            this.brushColor = color;
            this.updateBrushColorDisplay();
        }

        /**
         * Update brush size display
         */
        updateBrushSizeDisplay() {
            const display = document.getElementById(`brushSizeValue-${this.sectionId}`);
            if (display) {
                display.textContent = this.brushSize;
            }
            
            const slider = document.getElementById(`brushSizeSlider-${this.sectionId}`);
            if (slider) {
                slider.value = this.brushSize;
            }
        }

        /**
         * Update brush opacity display
         */
        updateBrushOpacityDisplay() {
            const display = document.getElementById(`brushOpacityValue-${this.sectionId}`);
            if (display) {
                display.textContent = this.brushOpacity + '%';
            }
            
            const slider = document.getElementById(`brushOpacitySlider-${this.sectionId}`);
            if (slider) {
                slider.value = this.brushOpacity;
            }
        }

        /**
         * Update brush smoothing display
         */
        updateBrushSmoothingDisplay() {
            const display = document.getElementById(`brushSmoothingValue-${this.sectionId}`);
            if (display) {
                display.textContent = Math.round(this.brushSmoothing * 100) + '%';
            }
            
            const slider = document.getElementById(`brushSmoothingSlider-${this.sectionId}`);
            if (slider) {
                slider.value = this.brushSmoothing * 100;
            }
        }

        /**
         * Update brush color display
         */
        updateBrushColorDisplay() {
            const preview = document.getElementById(`brushColorPreview-${this.sectionId}`);
            if (preview) {
                preview.style.background = this.brushColor;
            }
        }
    }

    // Export for use in other scripts
    window.BrushTool = BrushTool;
})();

